import { type FormEvent, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Check } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { ErrorBox } from '../components/ui/Card'
import { inputClass } from '../components/ui/FormField'

interface FieldErrors {
  password?: string
  confirm_password?: string
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [invalidLink, setInvalidLink] = useState(!token)

  const handleBlur = (field: keyof FieldErrors, value: string) => {
    const errors = { ...fieldErrors }
    if (field === 'password') {
      if (value.length < 8) errors.password = 'Password must be at least 8 characters'
      else delete errors.password
    }
    if (field === 'confirm_password') {
      if (value !== password) errors.confirm_password = 'Passwords do not match'
      else delete errors.confirm_password
    }
    setFieldErrors(errors)
  }

  const validateAll = (): boolean => {
    const errors: FieldErrors = {}
    if (password.length < 8) errors.password = 'Password must be at least 8 characters'
    if (confirmPassword !== password) errors.confirm_password = 'Passwords do not match'
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setApiError(null)
    if (!validateAll()) return
    setSubmitting(true)
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, new_password: password }),
      })
      if (res.status === 400) {
        setInvalidLink(true)
        return
      }
      if (!res.ok) {
        setApiError('Something went wrong. Please try again.')
        return
      }
      void navigate('/login?reset=success', { replace: true })
    } catch {
      setApiError('Network error. Please check your connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (invalidLink) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4">
        <div className="bg-surface rounded-xl shadow-card p-10 w-full max-w-md text-center">
          <div className="text-[3rem] mb-4">❌</div>
          <h1 className="m-0 mb-3 text-[1.3rem] font-bold text-text-heading">
            Link expired or invalid
          </h1>
          <p className="text-text-2 leading-[1.6] mb-6">
            This reset link has expired or has already been used. Request a new one.
          </p>
          <Link to="/forgot-password" className="text-accent font-semibold no-underline hover:underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-card p-10 w-full max-w-md">
        <div className="text-center font-bold text-[1.35rem] text-accent mb-1">Vet VPMS</div>
        <div className="text-center text-text-3 text-[0.9rem] mb-8">Choose a new password</div>

        {apiError && <ErrorBox>{apiError}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label className="block text-sm font-semibold text-text-heading mb-[0.35rem]" htmlFor="password">
            New password
          </label>
          <input
            id="password"
            className={`${inputClass(!!fieldErrors.password)} mb-1`}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={(e) => handleBlur('password', e.target.value)}
            autoComplete="new-password"
            disabled={submitting}
          />
          {fieldErrors.password
            ? <span className="text-danger-text text-[0.8rem] mb-[0.85rem] block">{fieldErrors.password}</span>
            : <span className="text-text-3 text-[0.8rem] mb-[0.85rem] block">
                {password.length > 0
                  ? password.length < 8
                    ? `${password.length}/8 characters minimum`
                    : <span className="inline-flex items-center gap-1"><Check size={14} strokeWidth={2.5} /> Password meets requirements</span>
                  : 'Minimum 8 characters'}
              </span>}

          <label className="block text-sm font-semibold text-text-heading mb-[0.35rem]" htmlFor="confirmPassword">
            Confirm new password
          </label>
          <input
            id="confirmPassword"
            className={`${inputClass(!!fieldErrors.confirm_password)} mb-1`}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onBlur={(e) => handleBlur('confirm_password', e.target.value)}
            autoComplete="new-password"
            disabled={submitting}
          />
          {fieldErrors.confirm_password
            ? <span className="text-danger-text text-[0.8rem] mb-[0.85rem] block">{fieldErrors.confirm_password}</span>
            : <span className="mb-[0.85rem] block" />}

          <Button type="submit" disabled={submitting} className="w-full mt-2">
            {submitting ? 'Updating…' : 'Set New Password'}
          </Button>
        </form>
      </div>
    </div>
  )
}
